import type {
  Bounds,
  LatLngObject,
  Place,
  PlacePrediction,
  StringRange,
} from "./model";

// Constants for maximum allowed place types and country codes
const MAX_PLACE_TYPES = 5;
const MAX_COUNTRY_CODES = 15;

const DEFAULT_PLACE_FIELDS = [
  "addressComponents",
  "formattedAddress",
  "location",
  "types",
];

const LEGACY_TO_NEW_PLACE_FIELDS: Record<string, string> = {
  address_components: "addressComponents",
  formatted_address: "formattedAddress",
  geometry: "location",
  name: "displayName",
  place_id: "id",
  types: "types",
};

export const escapeRegExp = (value: string) =>
  value.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");

const isLatLngLiteral = (
  value: Bounds
): value is Extract<Bounds, { north: number }> => "north" in value;

export const getBoundsLiteral = (bounds?: Bounds) => {
  if (!bounds) {
    return undefined;
  }

  if (isLatLngLiteral(bounds)) {
    return bounds;
  }

  return {
    east: bounds.getNorthEast().lng(),
    north: bounds.getNorthEast().lat(),
    south: bounds.getSouthWest().lat(),
    west: bounds.getSouthWest().lng(),
  };
};

export const getLocationLiteral = (location?: LatLngObject) =>
  location
    ? {
        lat: location.lat(),
        lng: location.lng(),
      }
    : undefined;

export const mapTypes = (types?: string[] | null) => {
  if (!types || types.length === 0) {
    return undefined;
  }

  return types.slice(0, MAX_PLACE_TYPES);
};

export const mapCountry = (country?: string | string[]) => {
  if (!country) {
    return undefined;
  }

  return (Array.isArray(country) ? country : [country])
    .filter(Boolean)
    .slice(0, MAX_COUNTRY_CODES);
};

export const mapPlaceFields = (fields?: string[] | null) => {
  if (!fields || fields.length === 0) {
    return DEFAULT_PLACE_FIELDS;
  }

  return Array.from(
    new Set([
      ...DEFAULT_PLACE_FIELDS,
      ...fields.map((field) => LEGACY_TO_NEW_PLACE_FIELDS[field] || field),
    ])
  );
};

const getRangeOffset = (range: StringRange) =>
  range.startOffset ?? range.offset ?? 0;

const getRangeLength = (range: StringRange) =>
  range.length ?? Math.max((range.endOffset ?? 0) - getRangeOffset(range), 0);

export const mapMatches = (matches?: StringRange[]) =>
  matches?.map((match) => ({
    length: getRangeLength(match),
    offset: getRangeOffset(match),
  }));

export const getSuggestionLabel = (placePrediction: PlacePrediction) => {
  const mainText = placePrediction?.mainText?.text || "";
  const secondaryText = placePrediction?.secondaryText?.text || "";
  const fullText = placePrediction?.text?.text;

  if (fullText) {
    return fullText;
  }

  return [mainText, secondaryText].filter(Boolean).join(", ");
};

const mapAddressComponents = (components?: Place["addressComponents"]) =>
  (components || []).map((component) => ({
    long_name: component.longText ?? component.long_name ?? "",
    short_name: component.shortText ?? component.short_name ?? "",
    types: component.types || [],
  }));

export const mapPlaceToLegacyResult = (place: Place) => ({
  address_components: mapAddressComponents(place.addressComponents),
  formatted_address:
    place.formattedAddress ||
    (typeof place.displayName === "string"
      ? place.displayName
      : place.displayName?.text) ||
    place.displayName ||
    "",
  name:
    (typeof place.displayName === "string"
      ? place.displayName
      : place.displayName?.text) || "",
  place_id: place.id,
  types: place.types || [],
  geometry: {
    location: place.location,
  },
});
